/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import {
    IAxisItem, ILevelsLayout, IState, IStateMetadata
} from '../../../../store/types/StateTypes';
import {
    IColumn,
    IColumnKind,
    IColumnType,
    IGroup,
    ITableVisualizationStaticProperties
} from '../types/tableVisualizationTypes';
import { IReducerAction, ITableVisualizationReducerActionData } from '../../../../store/types/ReducerTypes';
import { ITableMetadataComponent } from '../../../../store/types/MetadataTypes';

function extractLabel(item: IAxisItem) {
    const { label, aggregationFunctionName, defaultAggregationFunction } = item;
    let result = item.label;

    // the aggragation function name was added, should be removed
    if (aggregationFunctionName !== defaultAggregationFunction) {
        if (label[label.length - 1] === ')') {
            let depth = 1;
            let i = label.length - 2

            for (; depth && i >= 0; i -= 1) {
                if (label[i] === '(') {
                    depth -= 1;
                }

                if (label[i] === ')') {
                    depth += 1;
                }
            }

            if (i >= 0) {
                result = label.substr(0, i);
            }
        }
    }

    return result;
}

const findTableComponent = (metadata: IStateMetadata): ITableMetadataComponent => {
    const res = metadata.uiComponents.find((c) => c.componentType === 'table');

    if (!res) {
        throw new Error('Table UI component was not found');
    }

    return res as ITableMetadataComponent;
};

const makeColumns = (levelsLayout: ILevelsLayout, tableComponent: ITableMetadataComponent): IColumn[] => {
    const columnsLevels = levelsLayout.columns.reduce((memo, cItem) => {
        if (cItem.kind !== 'measures') {
            memo.push(cItem as IAxisItem);
        }
        return memo;
    }, [] as IAxisItem[]);

    return tableComponent.components
        .filter((comp) => (comp.componentType === 'column'))
        .map((component: ITableMetadataComponent, index: number) => {
            const column = columnsLevels.find((c) => (c.id || c.field) === component.properties.reference);
            let res: IColumn;

            if (column) {
                let label;
                switch (component.properties.label) {
                /* eslint-disable prefer-destructuring */
                case null: { label = column.id || column.field; break; }
                case undefined: { label = extractLabel(column); break; }
                default: { label = component.properties.label; }
                }

                res = {
                    id: column.id,
                    fieldName: column.field,
                    hierarchicalFieldName: column.hierarchicalName,
                    label,
                    kind: column.kind as IColumnKind,
                    aggregationFunction: column.aggregationFunctionName,
                    aggregationType: column.aggregationType || column.defaultAggregationType,
                    aggregationFormat: column.aggregationFormat,
                    timeBalanceFunctionName: column.timeBalanceFunctionName,
                    type: 'column' as IColumnType,
                    detailType: column.type,
                    detailFormat: column.format,
                    width: component.properties.width ? component.properties.width : 150,
                    showSummary: component.properties.showSummary ? component.properties.showSummary : false
                };
            } else {
                res = {
                    id: `_artificial_${index}`,
                    type: 'spacer',
                    width: component.properties.width,
                } as IColumn
            }

            return res;
        });
};

const makeGroups = (levelsLayout: ILevelsLayout): IGroup[] => {
    return levelsLayout.rows.map((row: any) => ({
        id: row.id,
        fieldName: row.field,
        hierarchicalFieldName: row.hierarchicalName,
        kind: row.kind,
        label: row.label,
        labelId: row.labelId,
        groupType: row.type,
        groupFormat: row.format
    }));
};

const shouldShowDetails = (tableComponent: ITableMetadataComponent): boolean => {
    let showDetails = true;
    const { properties } = tableComponent;

    if (properties) {
        showDetails = !!properties.showDetails
    }

    return showDetails;
};

const shouldShowTotals = (tableComponent: ITableMetadataComponent): boolean => {
    let showTotals = true;
    const { properties } = tableComponent;

    if (properties) {
        showTotals = !!properties.showTotals;
    }

    return showTotals;
};

function areFieldDistinct(tableUIComponent: ITableMetadataComponent) {
    return !!tableUIComponent.properties.showDistinct;
}

export default function tableVisualizationStaticPropertiesReducer(stateMetadata: any, newState: IState, action: IReducerAction<ITableVisualizationReducerActionData>): ITableVisualizationStaticProperties {
    const { levelsLayout } = newState;
    const tableUIComponent = findTableComponent(newState.metadata);

    return {
        showDetails: shouldShowDetails(tableUIComponent),
        showTotals: shouldShowTotals(tableUIComponent),
        distinctFields: areFieldDistinct(tableUIComponent),
        columns: makeColumns(levelsLayout, tableUIComponent),
        groups: makeGroups(levelsLayout),
        order: newState.orderInformation.levels
    };
}
